// ═══════════════════════════════════════════════════════════════
// VISTA: Simulación de Nómina
// Obtiene empleados desde el backend y simula localmente
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Play,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Users,
  Calculator,
  ChevronDown,
  ChevronUp,
  Loader
} from 'lucide-react';
import { usePayroll } from '../../../hooks/usePayroll';
import { getPayrollDetails } from '../../../api/payroll';
import api from '../../../api/config';

import Pagination from '../../common/Pagination';

export default function PayrollSimulationView({ payroll, onBack, onProcess }) {
  const { processing } = usePayroll();

  const [employees, setEmployees] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination for employee table
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 12;

  useEffect(() => {
    loadAndSimulate();
  }, [payroll.id]);

  const loadAndSimulate = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📥 Cargando detalles de nómina:', payroll.id);

      const details = await getPayrollDetails(payroll.id);

      console.log('✅ Detalles recibidos:', details);

      if (!details.success) {
        throw new Error(details.error || 'Error al obtener detalles');
      }

      const selectedEmployees = details.selectedEmployees || [];

      console.log('👥 Empleados a simular:', selectedEmployees.length);

      if (selectedEmployees.length === 0) {
        throw new Error('No hay empleados seleccionados en esta nómina');
      }

      setEmployees(selectedEmployees);

      await runSimulation(selectedEmployees);

    } catch (err) {
      console.error('❌ Error en simulación:', err);
      setError(err.message || 'Error al cargar datos');
      setSimulation({
        success: false,
        results: [],
        errors: [{ employeeName: 'Error', error: err.message }],
        summary: getSummaryTemplate()
      });
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async (employeeList) => {
    try {
      console.log('🧮 Enviando simulación al backend...');

      const response = await api.post(`/payroll/simulate`, {
        companyId: payroll.company_id,
        periodo: payroll.periodo,
        quincena: payroll.quincena,
        tipo: payroll.tipo,
        employees: employeeList
      });

      const result = response.data;

      console.log('✅ Simulación completada:', result);

      setSimulation({
        success: result.success,
        results: result.results || [],
        errors: result.errors || [],
        summary: result.summary || getSummaryTemplate()
      });

    } catch (error) {
      console.error('❌ Error en simulación:', error);
      throw error;
    }
  };

  const getSummaryTemplate = () => ({
    totalEmpleados: 0,
    totalErrores: 1,
    totalSalarioBruto: 0,
    totalIngresos: 0,
    totalDeducciones: 0,
    totalNeto: 0,
    totalCostoPatronal: 0,
    totalCostoEmpresa: 0,
    totalTSS: 0,
    totalISR: 0,
    totalPrestamos: 0
  });

  const toggleEmployeeDetails = (empId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [empId]: !prev[empId]
    }));
  };

  const handleProcessPayroll = () => {
    if (simulation && simulation.errors.length > 0) {
      if (!confirm(
        `Se encontraron ${simulation.errors.length} error(es). ¿Deseas continuar de todas formas?`
      )) {
        return;
      }
    }
    onProcess();
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // ── Loading state (inline, not full screen) ───────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Simulando nómina...</p>
        </div>
      </div>
    );
  }

  // ── Error state (inline, not full screen) ─────────────────────
  if (error || !simulation) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
            Volver
          </button>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-700 mb-1">Error al cargar simulación</p>
          <p className="text-sm text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  const { results, errors, summary } = simulation;

  // Pagination calculations
  const totalPages = Math.ceil(results.length / PER_PAGE);
  const start      = (currentPage - 1) * PER_PAGE;
  const currentResults = results.slice(start, start + PER_PAGE);

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver
        </button>
      </div>

      {/* ── Título + meta ──────────────────────────────────────── */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Simulación de Nómina</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {payroll.periodo} • {payroll.quincena || 'Mensual'} • {payroll.tipo}
        </p>
      </div>

      {/* ── Errores ────────────────────────────────────────────── */}
      {errors.length > 0 && (
        <div className="bg-white border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800 mb-2">
                Se encontraron {errors.length} error(es)
              </p>
              <div className="space-y-1.5">
                {errors.map((err, idx) => (
                  <div key={idx} className="bg-red-50 rounded-lg px-3 py-2">
                    <p className="text-xs font-semibold text-red-700">{err.employeeName}</p>
                    <p className="text-xs text-red-600">{err.error}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary Cards ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <Users size={18} className="text-blue-500" />
            <span className="text-2xl font-bold text-blue-600">{summary.totalEmpleados}</span>
          </div>
          <p className="text-xs text-gray-400 font-medium">Empleados</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp size={18} className="text-green-500" />
            <p className="text-sm font-bold text-green-600 text-right">
              RD$ {formatCurrency(summary.totalIngresos)}
            </p>
          </div>
          <p className="text-xs text-gray-400 font-medium">Total Ingresos</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <Calculator size={18} className="text-red-500" />
            <p className="text-sm font-bold text-red-600 text-right">
              RD$ {formatCurrency(summary.totalDeducciones)}
            </p>
          </div>
          <p className="text-xs text-gray-400 font-medium">Total Deducciones</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign size={18} className="text-blue-500" />
            <p className="text-sm font-bold text-blue-600 text-right">
              RD$ {formatCurrency(summary.totalNeto)}
            </p>
          </div>
          <p className="text-xs text-gray-400 font-medium">Total Neto</p>
        </div>
      </div>

      {/* ── Desglose de Totales ─────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calculator size={15} className="text-gray-400" />
          Desglose de Totales
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">Salario Bruto</p>
            <p className="text-base font-bold text-gray-900">
              RD$ {formatCurrency(summary.totalSalarioBruto)}
            </p>
          </div>
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">TSS (Total)</p>
            <p className="text-base font-bold text-orange-600">
              RD$ {formatCurrency(summary.totalTSS)}
            </p>
          </div>
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">ISR</p>
            <p className="text-base font-bold text-gray-700">
              RD$ {formatCurrency(summary.totalISR)}
            </p>
          </div>
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">Préstamos</p>
            <p className="text-base font-bold text-red-600">
              RD$ {formatCurrency(summary.totalPrestamos)}
            </p>
          </div>
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">Costo Patronal</p>
            <p className="text-base font-bold text-blue-600">
              RD$ {formatCurrency(summary.totalCostoPatronal)}
            </p>
          </div>
          <div className="border border-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">Costo Total Empresa</p>
            <p className="text-base font-bold text-indigo-600">
              RD$ {formatCurrency(summary.totalCostoEmpresa)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Detalle por Empleado ───────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">
            Detalle por Empleado
            <span className="ml-2 text-xs font-normal text-gray-400">{results.length} empleados</span>
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-gray-200">
                {['EMPLEADO', 'SALARIO BASE', 'INGRESOS', 'DEDUCCIONES', 'NETO', ''].map((h) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${h === '' ? 'text-center' : 'text-left'}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentResults.map((result, idx) => {
                const globalIdx = start + idx;
                return (
                  <React.Fragment key={globalIdx}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{result.employeeName}</p>
                        <p className="text-xs text-gray-400">{result.departamento}</p>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm text-gray-700">
                        RD$ {formatCurrency(result.salarioBase)}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm text-green-600">
                        RD$ {formatCurrency(result.totalIngresos)}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm text-red-600 font-semibold">
                        RD$ {formatCurrency(result.totalDeducciones)}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm font-bold text-blue-700">
                        RD$ {formatCurrency(result.netoAPagar)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleEmployeeDetails(globalIdx)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          {expandedEmployees[globalIdx] ? (
                            <ChevronUp size={15} className="text-gray-500" />
                          ) : (
                            <ChevronDown size={15} className="text-gray-500" />
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Fila expandible */}
                    {expandedEmployees[globalIdx] && (() => {
                      const iv = result.ingresosVariables || {};
                      const varItems = [
                        { label: 'Horas Extras',     val: iv.horas_extras   },
                        { label: 'Comisiones',        val: iv.comisiones     },
                        { label: 'Vacaciones',        val: iv.vacaciones     },
                        { label: 'Incentivos',        val: iv.incentivos     },
                        { label: 'Bono Desempeño',   val: iv.bonificaciones },
                        { label: 'Otros Ingresos',   val: iv.otros_ingresos },
                      ].filter(x => parseFloat(x.val || 0) > 0);
                      const hasVars = varItems.length > 0;
                      return (
                        <tr>
                          <td colSpan="6" className="px-4 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="space-y-4">

                              {/* Ingresos Variables */}
                              {hasVars && (
                                <div>
                                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Ingresos Variables</p>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                    {varItems.map(({ label, val }) => (
                                      <div key={label}>
                                        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                                        <p className="text-sm font-semibold text-green-600">
                                          RD$ {formatCurrency(val)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Deducciones */}
                              <div>
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Deducciones</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">TSS Empleado</p>
                                    <p className="text-sm font-semibold text-orange-600">
                                      RD$ {formatCurrency(result.tssEmpleado)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">ISR</p>
                                    <p className="text-sm font-semibold text-purple-600">
                                      RD$ {formatCurrency(result.isrQuincenal)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Préstamos</p>
                                    <p className="text-sm font-semibold text-red-600">
                                      RD$ {formatCurrency(result.descuentoPrestamo)}
                                    </p>
                                  </div>
                                  {parseFloat(result.otrasDeduccionesTotal || 0) > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-400 mb-0.5">Otras Deduc.</p>
                                      <p className="text-sm font-semibold text-red-600">
                                        RD$ {formatCurrency(result.otrasDeduccionesTotal)}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Costo Empresa */}
                              <div>
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Costo Empresa</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">TSS Empleador</p>
                                    <p className="text-sm font-semibold text-orange-600">
                                      RD$ {formatCurrency(result.tssEmpleador)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-400 mb-0.5">Costo Total</p>
                                    <p className="text-sm font-semibold text-gray-700">
                                      RD$ {formatCurrency(result.costoTotalEmpresa)}
                                    </p>
                                  </div>
                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          total={results.length}
          pageSize={PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ── Botones de acción ──────────────────────────────────── */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Volver
        </button>
        <button
          onClick={handleProcessPayroll}
          disabled={results.length === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play size={15} />
          Procesar Nómina ({results.length} empleados)
        </button>
      </div>
    </div>
  );
}
